import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Input,
  MenuItem,
  Popover,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import AppButton from "../../components/AppButton";
import { ReactNode, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { SX_FORM_CANCEL, SX_FORM_SUBMIT } from "../../Configuration/styles";
import { SCROLL_BAR_STYLES_THIN } from "../../Theme";
import { BacktestProperty, BacktestFilters as Filters } from "./services";

export interface FilterState {
  hebergementIds: number[];
  uhTypeIds: number[];
  month: string;
  dateOccupation: string;
}

const ALL = "";

/** "2026-04-15" parsed as a string is UTC midnight, which renders as the day before west of
 *  Greenwich — and daysOfMonth builds local dates, so value and label would disagree. */
const localDate = (iso: string) => {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
};

/** Days of the selected month, so the optional date picker never offers an empty scope. */
const daysOfMonth = (month: string, dateMin: string, dateMax: string) => {
  if (!month) return [];
  const [year, monthNumber] = month.split("-").map(Number);
  if (!year || !monthNumber) return [];

  const days: string[] = [];
  const cursor = new Date(year, monthNumber - 1, 1);
  while (cursor.getMonth() === monthNumber - 1) {
    const day = `${cursor.getFullYear()}-${String(
      cursor.getMonth() + 1
    ).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`;
    if (day >= dateMin && day <= dateMax) days.push(day);
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

/** One line of the list: a checkbox, its name, and the whole row as the hit target. */
const PropertyRow = ({
  checked,
  label,
  onClick,
  highlight = false,
}: {
  checked: boolean;
  label: string;
  onClick: () => void;
  highlight?: boolean;
}) => (
  <FormControlLabel
    sx={{
      py: 0.75,
      px: 2,
      m: 0,
      width: "100%",
      backgroundColor: highlight ? "action.selected" : "unset",
    }}
    slotProps={{ typography: { variant: "body2" } }}
    control={
      <Checkbox
        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
        checkedIcon={<CheckBoxIcon fontSize="small" />}
        color="primary"
        size="small"
        sx={{ mr: 1, p: 0 }}
        checked={checked}
        onClick={onClick}
      />
    }
    label={label}
  />
);

const POPOVER_SX = { "& > .MuiPopover-paper": { width: "340px" } };

/**
 * Property picker: a checkbox list behind a dropdown trigger. Selection is held aside until
 * Apply, so one refetch instead of one per tick — the backtest reads the whole test period.
 */
const IdPicker = ({
  options,
  selectedIds,
  onApply,
}: {
  options: BacktestProperty[];
  selectedIds: number[];
  onApply: (ids: number[]) => void;
}) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pending, setPending] = useState<number[]>([]);

  const everyId = options.map((item) => item.id);
  const checkAll = pending.length === options.length && options.length > 0;

  const label =
    selectedIds.length === 0
      ? `${intl.formatMessage({ id: "ALL" })} (${options.length})`
      : selectedIds.length === 1
      ? options.find((item) => item.id === selectedIds[0])?.name ?? "—"
      : `${selectedIds.length} / ${options.length}`;

  const handleValidate = () => {
    // Everything ticked is the same scope as nothing ticked, and the API reads an empty list
    // as "no filter". Normalising here keeps the reset button and the query cache honest.
    onApply(pending.length === options.length ? [] : pending);
    setAnchorEl(null);
  };

  return (
    <>
      <AppButton
        intent="secondary"
        disableRipple
        onClick={(event) => {
          // An empty selection means "every property"; the list shows it as every box ticked.
          setPending(selectedIds.length === 0 ? everyId : selectedIds);
          setAnchorEl(event.currentTarget);
        }}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          p: 0,
          minWidth: 0,
          // Full width with the caret pushed to the far edge, so this trigger lines up with
          // the two Selects next to it instead of hugging its own label.
          width: "100%",
          justifyContent: "space-between",
          textTransform: "none",
          fontSize: "0.875rem",
          fontWeight: 400,
          color: "text.primary",
          "&:hover": { backgroundColor: "transparent" },
          // Match the 24px caret of a MUI Select; a Button would draw it at 20px.
          "& .MuiButton-endIcon": {
            ml: 0,
            color: "action.active",
            "& > *:nth-of-type(1)": { fontSize: "1.5rem" },
          },
        }}
      >
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {label}
        </Box>
      </AppButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        sx={POPOVER_SX}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={() => setAnchorEl(null)}
      >
        <Box
          sx={{ maxHeight: 320, overflowY: "auto", ...SCROLL_BAR_STYLES_THIN }}
        >
          <PropertyRow
            checked={checkAll}
            highlight={checkAll}
            label={intl.formatMessage({ id: "HEBERGEMENT_CHECK_ALL" })}
            onClick={() => setPending(checkAll ? [] : everyId)}
          />
          {options.map((property) => (
            <PropertyRow
              key={property.id}
              checked={pending.includes(property.id)}
              label={property.name}
              onClick={() =>
                setPending((current) =>
                  current.includes(property.id)
                    ? current.filter((id) => id !== property.id)
                    : [...current, property.id]
                )
              }
            />
          ))}
        </Box>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            gap: 1,
            p: 1,
            boxSizing: "border-box",
          }}
        >
          <AppButton
            intent="tonal"
            sx={{ ...SX_FORM_CANCEL, m: 0 }}
            onClick={() => setAnchorEl(null)}
          >
            <FormattedMessage id="CANCEL" />
          </AppButton>
          <AppButton sx={{ ...SX_FORM_SUBMIT, m: 0 }} onClick={handleValidate}>
            <FormattedMessage id="SUBMIT" />
          </AppButton>
        </Box>
      </Popover>
    </>
  );
};

/**
 * One cell of the bar: its name above, its value below.
 *
 * The name sits on its own line rather than notching the field's outline, which is what made
 * the three controls read as three unrelated widgets of three different widths.
 */
const Segment = ({
  label,
  dimmed = false,
  compact = false,
  children,
}: {
  label?: string;
  dimmed?: boolean;
  compact?: boolean;
  children: ReactNode;
}) => (
  <Box
    sx={{
      px: compact ? 1 : 2,
      py: 1,
      minWidth: 0,
      // Same basis and same growth on every cell, so the free space splits evenly and the
      // separators land at regular intervals. Below the combined basis they wrap rather than
      // being squeezed under their own content.
      flex: compact ? "0 0 auto" : "1 1 200px",
      display: compact ? "flex" : "block",
      alignItems: "center",
      position: "relative",
      opacity: dimmed ? 0.45 : 1,
      // A hairline that fades out at both ends: a full-height rule cuts the bar into boxes,
      // this one only marks where one cell stops and the next begins.
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: "1px",
        background: (theme) =>
          `linear-gradient(to bottom, transparent, ${theme.vars.palette.divider} 28%, ${theme.vars.palette.divider} 72%, transparent)`,
      },
    }}
  >
    {label && (
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", display: "block", lineHeight: 1.5 }}
      >
        {label}
      </Typography>
    )}
    {children}
  </Box>
);

/** The two dropdowns of the bar: same borderless shape, only their options differ. */
const FilterSelect = ({
  value,
  options,
  onChange,
  renderValue,
  disabled = false,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (next: string) => void;
  renderValue: (selected: string) => string;
  disabled?: boolean;
}) => (
  <Select<string>
    variant="standard"
    input={<Input disableUnderline />}
    displayEmpty
    disabled={disabled}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    renderValue={renderValue}
    sx={{
      fontSize: "0.875rem",
      width: "100%",
      "& .MuiSelect-select": { p: 0, pr: 3 },
    }}
  >
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ))}
  </Select>
);

const BacktestFiltersBar = ({
  filters,
  value,
  onChange,
}: {
  filters: Filters;
  value: FilterState;
  onChange: (next: FilterState) => void;
}) => {
  const intl = useIntl();
  // Built locally on purpose: "2026-04" parsed as a string is UTC and slips a month west of
  // Greenwich.
  const monthLabel = (month: string) =>
    intl.formatDate(localDate(month), { month: "long", year: "numeric" });
  const dayLabel = (day: string) =>
    intl.formatDate(localDate(day), {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  const shortDate = (day: string | null) =>
    day ? intl.formatDate(localDate(day)) : "—";

  const days = daysOfMonth(
    value.month,
    filters.date_min ?? "",
    filters.date_max ?? ""
  );
  const isFiltered =
    value.hebergementIds.length > 0 ||
    value.uhTypeIds.length > 0 ||
    value.month !== ALL;
  const everything = intl.formatMessage({ id: "ALL" });
  const wholeMonth = intl.formatMessage({
    id: "ML_FORECAST_FILTER_WHOLE_MONTH",
  });

  return (
    <Paper
      elevation={1}
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "stretch" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", pl: 2 }}>
        <FilterAltOutlinedIcon
          fontSize="small"
          sx={{ color: "text.secondary" }}
        />
      </Box>

      <Segment
        label={intl.formatMessage({ id: "ML_FORECAST_FILTER_PROPERTIES" })}
      >
        <IdPicker
          options={filters.properties}
          selectedIds={value.hebergementIds}
          onApply={(hebergementIds) => onChange({ ...value, hebergementIds })}
        />
      </Segment>

      <Segment
        label={intl.formatMessage({ id: "ML_FORECAST_FEATURE_UH_TYPE" })}
      >
        <IdPicker
          options={filters.uh_types}
          selectedIds={value.uhTypeIds}
          onApply={(uhTypeIds) => onChange({ ...value, uhTypeIds })}
        />
      </Segment>

      <Segment label={intl.formatMessage({ id: "ML_FORECAST_FILTER_MONTH" })}>
        <FilterSelect
          value={value.month}
          options={[
            { value: ALL, label: everything },
            ...filters.months.map((month) => ({
              value: month,
              label: monthLabel(month),
            })),
          ]}
          // Changing month invalidates the day: it belonged to the previous one.
          onChange={(month) =>
            onChange({ ...value, month, dateOccupation: ALL })
          }
          renderValue={(selected) =>
            selected ? monthLabel(selected) : everything
          }
        />
      </Segment>

      <Segment
        label={intl.formatMessage({ id: "ML_FORECAST_FILTER_DATE" })}
        dimmed={!value.month}
      >
        <FilterSelect
          value={value.dateOccupation}
          disabled={!value.month}
          options={[
            { value: ALL, label: wholeMonth },
            ...days.map((day) => ({ value: day, label: dayLabel(day) })),
          ]}
          onChange={(dateOccupation) => onChange({ ...value, dateOccupation })}
          // The control is greyed out until a month is picked; saying so beats leaving the
          // user to guess why it will not open.
          renderValue={(selected) =>
            !value.month
              ? intl.formatMessage({
                  id: "ML_FORECAST_FILTER_PICK_MONTH_FIRST",
                })
              : selected
              ? dayLabel(selected)
              : wholeMonth
          }
        />
      </Segment>

      {/* Not a filter but the constraint the three above operate inside: every figure on the
          page comes from the test period. It belongs in the bar, not in a grey line under it. */}
      <Segment label={intl.formatMessage({ id: "ML_FORECAST_FILTER_SCOPE" })}>
        <Typography variant="body2">
          {intl.formatMessage(
            { id: "ML_FORECAST_FILTER_SCOPE_VALUE" },
            {
              from: shortDate(filters.date_min),
              to: shortDate(filters.date_max),
            }
          )}
        </Typography>
      </Segment>

      {isFiltered && (
        <Segment compact>
          <AppButton
            intent="secondary"
            size="small"
            onClick={() =>
              onChange({
                hebergementIds: [],
                uhTypeIds: [],
                month: ALL,
                dateOccupation: ALL,
              })
            }
          >
            {intl.formatMessage({ id: "ML_FORECAST_FILTER_RESET" })}
          </AppButton>
        </Segment>
      )}
    </Paper>
  );
};

export default BacktestFiltersBar;
